import React, { useEffect, useState } from "react";
import { Formik, Field, Form, ErrorMessage } from "formik";
import { FaRupeeSign } from "react-icons/fa";
import * as Yup from "yup";
import Swal from "sweetalert2";
import DatePicker from "react-datepicker";
import { useNavigate } from "react-router-dom";
import "./receiptModule.css";
import Navbar from "../navbar/navbar";
import { getGotraMasterList, getNakshatraMasterList, getPrathanaMasterList } from "../../services/admin.service";
import {
    fetchPrathanaReceiptDetails,
    formatISODate,
    generateReceipt,
    getBase64ImageFromUrl,
    getDevoteeMasterData,
} from "../../services/receipt.service";
import PrathanaReceiptPrint from "../../print/prathanaReceiptPrint";
import headerLogo from "../../assets/images/DailyPoojaReport.png";
import PaymentModalComponent from "./paymentModalComponent";
import { openRazorpayCheckout } from "./razorpayCheckout";
import {
    toNumber,
    getMinDate,
    getMaxDate,
    hasPaymentDetails,
    loadMasterList,
    buildPaymentDto,
    commonDevoteeSchema,
    MobileEntryCard,
    PaymentLoader,
    DevoteeDetailsSection,
    PaymentDetailsPreview,
    GrandTotal,
    PaymentTypeSelect,
    SubmitBar,
    getDefaultGotra,
    getDefaultNakshatra,
} from "./receiptShared";

const PrathanaReceiptComponent = () => {
    const roleName = localStorage.getItem("roleName");
    const username = localStorage.getItem("username");
    const isUser = roleName === "ROLE_USER";
    const isAdmin = roleName === "ROLE_ADMIN";
    const receiptsRoute = isUser ? "/user-receipts" : "/print-receipts";
    const navigate = useNavigate();

    const [mobileEntered, setMobileEntered] = useState(false);
    const [mobileNumber, setMobileNumber] = useState("");
    const [gotraMasterList, setGotraMasterList] = useState([]);
    const [nakshatraMasterList, setNakshatraMasterList] = useState([]);
    const [prathanaList, setPrathanaList] = useState([]);
    const [devoteeData, setDevoteeData] = useState(null);
    const [selectedPaymentType, setSelectedPaymentType] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    /* ------------------------- data loading ------------------------- */

    useEffect(() => {
        const fetchData = async () => {
            if (isUser && username) {
                setMobileNumber(username);
                setMobileEntered(true);
                try {
                    const result = await getDevoteeMasterData(username);
                    setDevoteeData(result || {});
                } catch (error) {
                    console.error("Error fetching user data:", error);
                }
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        loadMasterList(getGotraMasterList, setGotraMasterList, "Gotra");
        loadMasterList(getNakshatraMasterList, setNakshatraMasterList, "Nakshatra");
        loadMasterList(getPrathanaMasterList, setPrathanaList, "Prathana");
    }, []);

    const handleMobileSubmit = async () => {
        if (mobileNumber.length !== 10) {
            Swal.fire("Error", "Please enter a valid 10-digit mobile number", "error");
            return;
        }
        setLoading(true);
        try {
            let result = null;
            try {
                result = await getDevoteeMasterData(mobileNumber);
            } catch (err) {
                console.warn("Devotee not found or error fetching devotee details:", err);
                result = null;
            }
            setDevoteeData(result || {});
            setMobileEntered(true);
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Could not load devotee details. Please try again.", "error");
        } finally {
            setLoading(false);
        }
    };

    const isDevoteeNameDisabled = Boolean(devoteeData?.nameOfDevotee && devoteeData.nameOfDevotee.trim());

    /* ------------------------- form config ------------------------- */

    const initialValues = {
        devoteeName: devoteeData?.nameOfDevotee || "",
        mobile: devoteeData?.mobileNo || mobileNumber,
        alternateMobile: devoteeData?.alternateNo || "",
        nakshatra: getDefaultNakshatra(devoteeData, nakshatraMasterList),
        gotra: getDefaultGotra(devoteeData, gotraMasterList),
        prathanaAmount: prathanaList.length > 0 ? prathanaList[0].rate : 0,
        prathanaRemarks: "",
        prathanaOther: 0,
        poojaType1: "98",
        poojaDateTime1: null,
        paymentType: isUser ? "Online" : "",
        paymentDetails: {},
    };

    const validationSchema = Yup.object({
        ...commonDevoteeSchema,
        poojaDateTime1: Yup.date().typeError("Prathana Date is required").required("Prathana Date is required"),
        prathanaOther: Yup.number().typeError("Must be a number").min(0, "Must be >= 0"),
        prathanaRemarks: Yup.string().max(100, "Must be 100 characters or less").trim(),
    });

    const nakshatraOptions = nakshatraMasterList.map((item) => ({
        value: item.nakshatraId,
        label: item.nakshatraName,
    }));

    const gotraOptions = gotraMasterList.map((item) => ({
        value: item.gotraId,
        label: item.gotraName,
    }));

    const calculateTotal = (values) => toNumber(values.prathanaAmount) + toNumber(values.prathanaOther);

    /* ------------------------- handlers ------------------------- */

    const handleDateChange = async (date, fieldName, setFieldValue) => {
        if (!date) {
            setFieldValue(fieldName, null);
            return;
        }
        try {
            const selectedDateStr = date.toLocaleDateString("en-CA");
            const list = (await fetchPrathanaReceiptDetails()) || [];
            const alreadyBooked = list.find((item) => item.poojaDateTime === selectedDateStr);
            if (alreadyBooked) {
                Swal.fire({
                    icon: "warning",
                    title: "Prathana Alert!",
                    text: "Already prathana booked for this day!",
                });
                setFieldValue(fieldName, null);
            } else {
                setFieldValue(fieldName, formatISODate(date));
            }
        } catch (error) {
            console.error("Error checking prathana bookings:", error);
            Swal.fire("Error", "Could not check existing bookings. Please try again.", "error");
            setFieldValue(fieldName, null);
        }
    };

    const handlePaymentTypeChange = (e, setFieldValue) => {
        const value = e.target.value;
        setFieldValue("paymentType", value);
        setSelectedPaymentType(value);
        if (["CQ", "RT"].includes(value)) {
            setShowModal(true);
        } else {
            setFieldValue("paymentDetails", {});
        }
    };

    const handleCancel = (resetForm) => {
        if (resetForm) resetForm();
        setMobileEntered(false);
        setMobileNumber("");
        setDevoteeData(null);
        setSelectedPaymentType("");
    };

    const finishAndLeave = (resetForm) => {
        resetForm();
        setMobileEntered(false);
        setMobileNumber("");
        setDevoteeData(null);
        navigate(receiptsRoute);
    };

    const handleSubmit = async (values, { resetForm }) => {
        if (!values.paymentType) {
            Swal.fire("Payment Type Required", "Please select a payment type.", "warning");
            return;
        }

        // ---- Cash / Cheque / RTGS ----
        if (["CA", "CQ", "RT"].includes(values.paymentType)) {
            const requiresDetails = ["CQ", "RT"].includes(values.paymentType);
            if (requiresDetails && !hasPaymentDetails(values.paymentDetails)) {
                Swal.fire("Payment Details Required", "Please enter the cheque/RTGS payment details.", "warning");
                return;
            }

            const confirmResult = await Swal.fire({
                title: "Generate Receipt?",
                text: "Are you sure you want to generate this Prathana receipt?",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Yes, Generate",
                cancelButtonText: "Cancel",
            });
            if (!confirmResult.isConfirmed) return;

            setLoading(true);
            try {
                const response = await generateReceipt(values);
                if (!response) {
                    Swal.fire("Error", "Failed to generate receipt. Please try again.", "error");
                    return;
                }
                const base64Logo = await getBase64ImageFromUrl(headerLogo);
                await PrathanaReceiptPrint(response, base64Logo, nakshatraMasterList, gotraMasterList);
                finishAndLeave(resetForm);
            } catch (error) {
                console.error("Error generating receipt:", error);
                Swal.fire("Error", "Error generating receipt. Please try again.", "error");
            } finally {
                setLoading(false);
            }
            return;
        }

        // ---- Online (Razorpay) ----
        if (values.paymentType === "Online") {
            const totalAmount = calculateTotal(values);
            if (!totalAmount || totalAmount <= 0) {
                Swal.fire("Invalid Amount", "Total amount must be greater than zero for online payment.", "warning");
                return;
            }

            const onPaymentSuccess = async (paymentData, pdfWindow) => {
                setLoading(true);
                try {
                    const receiptData = { ...values, paymentDetails: buildPaymentDto(paymentData) };
                    const response = await generateReceipt(receiptData);
                    if (!response) {
                        if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
                        Swal.fire("Error", "Failed to generate receipt. Please contact temple office.", "error");
                        return;
                    }
                    const base64Logo = await getBase64ImageFromUrl(headerLogo);
                    await PrathanaReceiptPrint(response, base64Logo, nakshatraMasterList, gotraMasterList, pdfWindow);
                    finishAndLeave(resetForm);
                } catch (error) {
                    console.error("Error generating receipt after payment:", error);
                    if (pdfWindow && !pdfWindow.closed) pdfWindow.close();
                    Swal.fire("Error", "Payment succeeded, but receipt generation encountered an error. Please contact temple office.", "error");
                } finally {
                    setLoading(false);
                }
            };

            await openRazorpayCheckout(
                totalAmount,
                values,
                setLoading,
                onPaymentSuccess,
                "Prathana Receipt Payment"
            );
            return;
        }

        Swal.fire("Invalid Payment Type", "Please select a valid payment type.", "warning");
    };

    /* ------------------------- render ------------------------- */

    return (
        <div className="card receipt-page">
            <Navbar />
            <div className="receipt-heading">
                <h3 className="custom-heading">Prathana Receipt</h3>
            </div>

            <div className="receipt-content">
                {!mobileEntered ? (
                    <MobileEntryCard
                        mobileNumber={mobileNumber}
                        setMobileNumber={setMobileNumber}
                        onSubmit={handleMobileSubmit}
                    />
                ) : (
                    <>
                        {loading && <PaymentLoader text="Processing, please wait..." />}
                        <Formik
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={handleSubmit}
                            enableReinitialize={true}
                        >
                            {({ setFieldValue, values, resetForm }) => (
                                <>
                                    <Form autoComplete="off">
                                        <div className="card receipt-form-card">
                                            <DevoteeDetailsSection
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                nakshatraOptions={nakshatraOptions}
                                                gotraOptions={gotraOptions}
                                                isNameDisabled={isDevoteeNameDisabled}
                                                showCancel={!isUser}
                                                onCancel={() => handleCancel(resetForm)}
                                            />

                                            <hr className="my-4" />
                                            <h4 className="section-title pooja">Prathana Details</h4>
                                            <div className="row g-3 text-start">
                                                <div className="col-md-2">
                                                    <label className="form-label">Prathana Amount</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text"><FaRupeeSign /></span>
                                                        <Field name="prathanaAmount" className="form-control" type="number" readOnly />
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label">Other Charges</label>
                                                    <div className="input-group">
                                                        <span className="input-group-text"><FaRupeeSign /></span>
                                                        <Field name="prathanaOther" className="form-control" type="number" />
                                                    </div>
                                                    <ErrorMessage name="prathanaOther" component="div" className="text-danger" />
                                                </div>
                                                <div className="col-md-2">
                                                    <label className="form-label">Prathana Date</label>
                                                    <DatePicker
                                                        id="poojaDateTime1"
                                                        name="poojaDateTime1"
                                                        placeholderText="Select Date"
                                                        selected={values.poojaDateTime1}
                                                        onChange={(date) => handleDateChange(date, "poojaDateTime1", setFieldValue)}
                                                        className="form-control"
                                                        dateFormat="dd-MM-yyyy"
                                                        showYearDropdown
                                                        showMonthDropdown
                                                        dropdownMode="select"
                                                        minDate={getMinDate()}
                                                        maxDate={getMaxDate()}
                                                        onKeyDown={(event) => event.preventDefault()}
                                                    />
                                                    <ErrorMessage name="poojaDateTime1" component="div" className="text-danger" />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="form-label">Remarks</label>
                                                    <Field name="prathanaRemarks" className="form-control" />
                                                    <ErrorMessage name="prathanaRemarks" component="div" className="text-danger" />
                                                </div>
                                            </div>

                                            {(isAdmin || isUser) && (
                                                <div className="row g-3 mt-4 align-items-end">
                                                    <div className="col-md-6">
                                                        {isAdmin && <PaymentDetailsPreview paymentDetails={values.paymentDetails} />}
                                                    </div>
                                                    <div className="col-md-6">
                                                        <div className="row g-3 justify-content-end">
                                                            <div className="col-sm-6">
                                                                <GrandTotal amount={calculateTotal(values)} />
                                                            </div>
                                                            {isAdmin && (
                                                                <div className="col-sm-6">
                                                                    <PaymentTypeSelect
                                                                        onChange={(e) => handlePaymentTypeChange(e, setFieldValue)}
                                                                        onEditDetails={() => setShowModal(true)}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            <SubmitBar
                                                paymentType={values.paymentType}
                                                loading={loading}
                                                showCancel={!isUser}
                                                onCancel={() => handleCancel(resetForm)}
                                            />
                                        </div>
                                    </Form>

                                    {showModal && (
                                        <PaymentModalComponent
                                            selectedPaymentType={selectedPaymentType}
                                            onClose={() => setShowModal(false)}
                                            setPaymentDetails={(data) => {
                                                setFieldValue("paymentDetails", data);
                                                setShowModal(false);
                                            }}
                                        />
                                    )}
                                </>
                            )}
                        </Formik>
                    </>
                )}
            </div>
        </div>
    );
};

export default PrathanaReceiptComponent;
